import cn from 'classnames';
import uniqueId from 'lodash-es/uniqueId';
import * as React from 'react';
import { DetailedHTMLProps, TextareaHTMLAttributes, useState } from 'react';
import { useCommonInputStyles } from '@tcl-boron-foundations/input';
import { BaseInputProps } from '@tcl-argon-models/models';
import { mergeClasses, StatusVariant } from '@tcl-argon-utils/utils';
import { useTextAreaStyles } from './styles';
import { useScrollbarStyles } from '@tcl-boron-styles/hooks';

type HTMLTextAreaProps = DetailedHTMLProps<TextareaHTMLAttributes<HTMLTextAreaElement>, HTMLTextAreaElement>;

export interface TextAreaProps extends Omit<BaseInputProps, 'theme'>, Omit<HTMLTextAreaProps, 'onChange'> {
  /** A handler called whenever this input changes. The new value is passed as the only argument to the callback. */
  onChange: (value: string) => void;
  /** style will change on input based on textAreaType */
  textAreaType: 'primary' | 'secondary';
  /** input status will affect colors that reflect that status. 'error' | 'warning' | 'success' | 'default' */
  status?: StatusVariant;
}

export const TextArea: React.FC<TextAreaProps> = ({
  className = '',
  classes: classOverrides = {},
  disabled = false,
  hideLabel = false,
  id,
  label,
  onChange,
  subContent,
  textAreaType = 'primary',
  status = 'default',
  ...props
}) => {
  [id] = useState(id || uniqueId('text_area_'));
  const classes = mergeClasses(
    useCommonInputStyles({ status, subContent }),
    useTextAreaStyles({ status, onChange, textAreaType, label }),
    useScrollbarStyles(),
    classOverrides,
    { root: className },
  );

  return (
    <div className={classes.root} data-analytics="textarea">
      <label htmlFor={id} className={cn(classes.label, { [classes.isVisuallyHidden]: hideLabel })}>
        {label}
      </label>

      <textarea
        className={cn(classes.textArea, classes.scrollbar, {
          [classes.disabled]: disabled,
        })}
        data-analytics="text-area"
        onChange={(e) => onChange(e.target.value)}
        aria-disabled={disabled}
        {...props}
      />
      {subContent && <p className={classes.subContent}>{subContent}</p>}
    </div>
  );
};
